import React from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
  Form,
  FormButtons,
  FormField,
  Button,
  Heading,
  Typography,
  useCall,
  useQuery,
  useFetch,
} from "@gemlightbox/core-kit";

import { getCheckResetToken, putResetPassword } from "src/api";
import { ErrorCodes, LOGIN_PAGE } from "src/constants";
import { useStores } from "src/hooks";
import { initialNewPasswordResetForm } from "./password.constants";
import { NewPasswordResetForm } from "./password.types";
import { requestPasswordResetValidate } from "./password.utils";

import styles from "./password.module.css";

export const PasswordPage: React.FC = observer(() => {
  const navigate = useNavigate();

  const { localeStore } = useStores();

  const {
    query: { guid = "" },
  } = useQuery<{ guid?: string }>();

  const isGuidEmpty = !guid?.trim();

  const { loading, onFetchSuccess } = useFetch(getCheckResetToken.setParams({ guid }), {
    disabled: isGuidEmpty,
  });
  onFetchSuccess((result) => {
    if (!result) handleExpired();
  });

  const { submit, onCallSuccess, onCallError } = useCall(putResetPassword);
  onCallSuccess(() => navigate(LOGIN_PAGE.path));
  onCallError(({ originalError }) => {
    if (originalError?.code === ErrorCodes.USER_RESET_PASSWORD_TOKEN_EXPIRED) {
      handleExpired();
    }
  });

  const handleExpired = () => navigate("/auth/reset/expired");

  const handleSubmit = async ({ password }: NewPasswordResetForm) => {
    await submit({
      params: { guid },
      data: { password },
    });
  };

  if (isGuidEmpty) return <Navigate to={LOGIN_PAGE.path} />;

  return (
    <div className={styles.filler}>
      <div className={styles.passwordPageContainer}>
        <Form
          className={styles.passwordPageFormContainer}
          contentClassName={styles.passwordPageWrapper}
          initialValues={initialNewPasswordResetForm}
          validate={requestPasswordResetValidate}
          onSubmit={handleSubmit}
          loading={loading}
        >
          <Heading tag="h2" color="textSecondary">
            {localeStore.t("auth.reset.password.title")}
          </Heading>

          <Typography className={styles.message} size="small" color="textTertiary">
            {localeStore.t("auth.reset.password.subtitle")}
          </Typography>

          <FormField
            className={styles.passwordContainer}
            type="password"
            name="password"
            label={localeStore.t("auth.reset.password.fields.password.label")}
            placeholder={localeStore.t("auth.reset.password.fields.password.placeholder")}
            appearance="primaryV2"
            bottomMessageAlign="opposite-of-error"
            bottomMessage={localeStore.t('auth.reset.password.fields.password["bottom-message"]')}
          />

          <FormField
            className={styles.confirmPasswordContainer}
            type="password"
            name="confirmPassword"
            label={localeStore.t('auth.reset.password.fields["confirm-password"].label')}
            placeholder={localeStore.t(
              'auth.reset.password.fields["confirm-password"].placeholder',
            )}
            appearance="primaryV2"
          />

          <FormButtons className={styles.buttonsWrapper} align="column">
            <Button type="submit">{localeStore.t("auth.reset.password.buttons.save")}</Button>
          </FormButtons>
        </Form>
      </div>
    </div>
  );
});

export default PasswordPage;
